'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgXmark from '@/icons/regular/Xmark'

import { purchaseLabel } from './purchase-label'

import { ActionType, type DirectoryAction, type DirectoryFilters, type FilterArrayKey } from '../use-directory-reducer'

interface ActiveFiltersProps {
  filters: DirectoryFilters
  facetLabels: Map<string, string>
  dispatch: React.Dispatch<DirectoryAction>
}

// One removable pill per active filter value, plus "Clear filters". Values
// show their localized facet label; brand slugs resolve through the same map.
export const ActiveFilters = ({ filters, facetLabels, dispatch }: ActiveFiltersProps) => {
  const { t } = useTranslation()

  const axisLabels: Record<FilterArrayKey | 'brand', string> = {
    classification: t('directory.filterLabel.classification', 'Type'),
    tags: t('directory.filterLabel.tags', 'Best for'),
    channels: t('directory.filterLabel.channels', 'Purchase'),
    accessories: t('directory.filterLabel.accessories', 'Accessories'),
    hardware: t('directory.filterLabel.hardware', 'Hardware'),
    software: t('directory.filterLabel.software', 'Software'),
    loss: t('directory.filterLabel.loss', 'Hearing loss'),
    style: t('directory.filterLabel.style', 'Style'),
    brand: t('directory.filterLabel.brand', 'Brand'),
  }

  const valueLabel = (key: keyof DirectoryFilters, value: string): string =>
    key === 'channels' ? purchaseLabel(value, t) : (facetLabels.get(value) ?? value)

  const pills: { key: string; label: string; onRemove: () => void }[] = []
  if (filters.brand) {
    pills.push({
      key: `brand:${filters.brand}`,
      label: `${axisLabels.brand}: ${valueLabel('brand', filters.brand)}`,
      onRemove: () => dispatch({ type: ActionType.SetBrand, value: filters.brand }),
    })
  }
  for (const [axis, values] of Object.entries(filters)) {
    if (axis === 'brand' || !Array.isArray(values)) {
      continue
    }
    const key = axis as FilterArrayKey
    for (const value of values) {
      pills.push({
        key: `${key}:${value}`,
        label: `${axisLabels[key]}: ${valueLabel(key, value)}`,
        onRemove: () => dispatch({ type: ActionType.ToggleFilter, key, value }),
      })
    }
  }

  if (pills.length === 0) {
    return null
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {pills.map((pill) => (
        <span
          key={pill.key}
          className="border-navy/20 text-navy inline-flex min-h-[36px] items-center gap-1 rounded-full border bg-white py-1 pr-1 pl-3 text-[13px] font-medium"
        >
          {pill.label}
          <button
            type="button"
            data-tracking="ignore"
            aria-label={t('client.filters.removeFilter', 'Remove filter: {{label}}', { label: pill.label })}
            onClick={pill.onRemove}
            className="hover:bg-navy/10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full"
          >
            <SvgXmark className="h-3 w-3 fill-current" aria-hidden />
          </button>
        </span>
      ))}
      <button
        type="button"
        data-tracking="ignore"
        onClick={() => dispatch({ type: ActionType.ClearFilters })}
        className="text-lapis hover:text-navy ml-1 cursor-pointer text-[14px] font-medium underline decoration-dotted underline-offset-4"
      >
        {t('directory.clearFilters', 'Clear filters')}
      </button>
    </div>
  )
}
